import React, { useEffect } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import SEO from '../components/SEO';
import GradientOverlay from '../components/GradientOverlay';
import LogomarkPattern from '../components/LogomarkPattern';

const Contact = () => {
  useEffect(() => {
    // Check if form already exists to prevent duplicates
    const container = document.getElementById('hubspot-form-container');
    if (container && container.children.length > 0) {
      return; // Form already exists
    }

    // Load HubSpot Forms API v2 script
    const script = document.createElement('script');
    script.src = '//js.hsforms.net/forms/embed/v2.js';
    script.charset = 'utf-8';
    script.async = true;
    
    script.onload = () => {
      // Create form using JavaScript API (renders in DOM, not iframe)
      if (window.hbspt) {
        // Clear container first to prevent duplicates
        const formContainer = document.getElementById('hubspot-form-container');
        if (formContainer) {
          formContainer.innerHTML = '';
        }
        
        window.hbspt.forms.create({
          region: 'ap1',
          portalId: '442599135',
          formId: 'faeacfbb-329d-446a-92dc-6c86008607c5',
          target: '#hubspot-form-container',
          css: '',
          onFormReady: function($form) {
            console.log('HubSpot form loaded successfully');
          }
        });
      }
    };
    
    // Check if script already exists
    const existingScript = document.querySelector('script[src*="hsforms.net"]');
    if (existingScript) {
      // Script already loaded, just create the form
      if (window.hbspt) {
        const formContainer = document.getElementById('hubspot-form-container');
        if (formContainer) {
          formContainer.innerHTML = '';
        }
        window.hbspt.forms.create({
          region: 'ap1',
          portalId: '442599135',
          formId: 'faeacfbb-329d-446a-92dc-6c86008607c5',
          target: '#hubspot-form-container',
          css: '',
          onFormReady: function($form) {
            console.log('HubSpot form loaded successfully');
          }
        });
      }
    } else {
      document.body.appendChild(script);
    }

    return () => {
      // Clear the form container on unmount
      const formContainer = document.getElementById('hubspot-form-container');
      if (formContainer) {
        formContainer.innerHTML = '';
      }
    };
  }, []);

  return (
    <div style={{ marginTop: '80px' }}>
      <SEO 
        title="Contact Us - Get Started Today"
        description="Contact Resolv Digital for a complimentary assessment of your CRM, ERP, or AI needs. Gold Coast's trusted digital transformation consultancy."
        keywords="Contact Resolv Digital, CRM Consultation Gold Coast, ERP Consultation Brisbane, AI Consultation Australia, Digital Transformation Quote"
        canonical="https://resolvdigital.com.au/contact"
      />
      
      {/* Hero Section - LIGHT */}
      <section style={{
        minHeight: '50vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#ffffff',
        padding: '120px clamp(20px, 5vw, 40px) 80px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Gradient Overlay */}
        <GradientOverlay variant="subtle" opacity={0.15} />
        
        {/* Logomark Pattern */}
        <LogomarkPattern opacity={0.03} density="sparse" tint="teal" />
        
        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '8%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(13, 115, 119, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        
        <div style={{
          position: 'absolute',
          bottom: '20%',
          left: '5%',
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(201, 162, 39, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />

        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h1 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.25rem, 6vw, 5rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #0D7377 50%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '32px',
            wordBreak: 'break-word'
          }}>
            Let's Resolve Your Challenges
          </h1>
          <p style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.5rem',
            lineHeight: 1.6,
            color: '#0a4447',
            maxWidth: '800px'
          }}>
            Book a complimentary assessment call and discover a clear path forward for your business.
          </p>
        </div>
      </section>

      {/* Contact Content - LIGHT TEAL */}
      <section style={{
        padding: 'clamp(40px, 8vw, 80px) clamp(20px, 5vw, 40px)',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LogomarkPattern opacity={0.02} density="sparse" tint="teal" />
        
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
            gap: 'clamp(40px, 8vw, 80px)'
          }}>
            {/* Contact Information */}
            <div>
              <h2 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '2rem',
                background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textTransform: 'uppercase',
                marginBottom: '32px'
              }}>
                Get in Touch
              </h2>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '32px',
                marginBottom: '48px'
              }}>
                <div style={{
                  display: 'flex',
                  gap: '20px',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#0D7377',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Mail size={24} style={{ color: '#ffffff' }} />
                  </div>
                  <div>
                    <h3 style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontWeight: 600,
                      fontSize: '1.125rem',
                      color: '#0a4447',
                      marginBottom: '8px'
                    }}>
                      Email
                    </h3>
                    <a href="mailto:info@resolvdigital.com.au" style={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '1rem',
                      color: '#0D7377',
                      textDecoration: 'none',
                      transition: 'opacity 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                    >
                      info@resolvdigital.com.au
                    </a>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '20px',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#0D7377',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Phone size={24} style={{ color: '#ffffff' }} />
                  </div>
                  <div>
                    <h3 style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontWeight: 600,
                      fontSize: '1.125rem',
                      color: '#0a4447',
                      marginBottom: '8px'
                    }}>
                      Phone
                    </h3>
                    <a href="tel:+61428814091" style={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '1rem',
                      color: '#0D7377',
                      textDecoration: 'none',
                      transition: 'opacity 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                    >
                      +61 428 814 091
                    </a>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '20px',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#0D7377',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <MapPin size={24} style={{ color: '#ffffff' }} />
                  </div>
                  <div>
                    <h3 style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontWeight: 600,
                      fontSize: '1.125rem',
                      color: '#0a4447',
                      marginBottom: '8px'
                    }}>
                      Location
                    </h3>
                    <p style={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '1rem',
                      color: '#4a5568',
                      lineHeight: 1.6
                    }}>
                      Gold Coast, Queensland<br />
                      Australia
                    </p>
                  </div>
                </div>
              </div>

              <div style={{
                backgroundColor: '#ffffff',
                border: '2px solid #0D7377',
                borderRadius: '8px',
                padding: 'clamp(20px, 5vw, 32px)',
                boxShadow: '0 8px 32px rgba(13, 115, 119, 0.1)'
              }}>
                <h3 style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  color: '#0a4447',
                  marginBottom: '16px'
                }}>
                  Office Hours
                </h3>
                <p style={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1rem',
                  color: '#0a4447',
                  lineHeight: 1.8
                }}>
                  <strong>Monday - Friday:</strong> 9:00 AM - 5:30 PM AEST<br />
                  <strong>Saturday - Sunday:</strong> Closed
                </p>
              </div>
            </div>

            {/* HubSpot Contact Form */}
            <div style={{
              backgroundColor: '#ffffff',
              border: '1px solid rgba(13, 115, 119, 0.2)',
              borderRadius: '8px',
              padding: 'clamp(24px, 5vw, 48px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
            }}>
              <h2 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '2rem',
                color: '#0a4447',
                marginBottom: '32px'
              }}>
                Send Us a Message
              </h2>

              {/* HubSpot Form Container */}
              <div 
                id="hubspot-form-container"
                style={{
                  minHeight: '400px'
                }}
              ></div>

              {/* Custom styling for HubSpot form on light background */}
              <style>{`
                /* HubSpot Form Container */
                #hubspot-form-container {
                  font-family: 'Lato', sans-serif !important;
                }

                /* Required asterisk - brand gold color */
                #hubspot-form-container .hs-form-required {
                  color: #C9A227 !important;
                }

                /* Submit button styling - brand colors */
                #hubspot-form-container input[type="submit"],
                #hubspot-form-container button[type="submit"],
                #hubspot-form-container .hs-button {
                  background-color: #0D7377 !important;
                  color: #ffffff !important;
                  border: none !important;
                  border-radius: 10rem !important;
                  padding: 1em 2.5em !important;
                  font-family: 'Lato', sans-serif !important;
                  font-weight: 700 !important;
                  font-size: 1rem !important;
                  text-transform: uppercase !important;
                  cursor: pointer !important;
                  transition: all 0.3s ease !important;
                  min-height: 48px !important;
                  margin-top: 16px !important;
                }

                #hubspot-form-container input[type="submit"]:hover,
                #hubspot-form-container button[type="submit"]:hover,
                #hubspot-form-container .hs-button:hover {
                  background-color: #0a5d60 !important;
                  transform: scale(1.02) !important;
                  box-shadow: 0 4px 20px rgba(13, 115, 119, 0.4) !important;
                }

                /* Error messages */
                #hubspot-form-container .hs-error-msg,
                #hubspot-form-container .hs-error-msgs {
                  color: #dc3545 !important;
                  font-size: 0.875rem !important;
                }

                /* Field spacing */
                #hubspot-form-container .hs-form-field {
                  margin-bottom: 16px !important;
                }
              `}</style>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
